import { sanitize } from './sanitize';
import { SanitizeOptions } from './whitelist';

export type PasteGuardMode = 'strict' | 'relaxed' | 'off';

export interface PasteGuardConfig extends SanitizeOptions {
    // 'strict' filters foreign pastes against the whitelist. 'relaxed' uses the
    // same whitelist but keeps more formatting: fonts, sizes, background
    // colours, spacing, indents and borders (SanitizeOptions.relaxed). 'off'
    // turns the guard off, but only as a fixed value in the code. A function is
    // asked on every paste, so the mode can change while the editor runs (the
    // paste filter switch in the invigilator menu); it can go no further than
    // 'relaxed'.
    mode?: PasteGuardMode | (() => PasteGuardMode);
}

// When the mode function fails or returns an unknown value, the guard stays strict.
function currentMode(config: PasteGuardConfig): 'strict' | 'relaxed' {
    let mode: unknown = config.mode;
    if (typeof mode === 'function') {
        try {
            mode = mode();
        } catch {
            return 'strict';
        }
    }
    return mode === 'relaxed' || mode === 'off' ? 'relaxed' : 'strict';
}

// Structural subsets of the CKEditor types this module uses. Keeping the
// CKEditor packages out of the imports lets the node tests load this module
// without the webpack bundle.
export interface HtmlProcessor<Fragment> {
    toData(fragment: Fragment): string;
    toView(html: string): Fragment;
}

export interface InputTransformationData<Fragment> {
    content: Fragment;
    dataTransfer?: { getData(type: string): string };
}

export interface StoppableEvent {
    stop(): void;
}

export interface InputTransformationEmitter<Fragment> {
    on(
        event: 'inputTransformation',
        callback: (evt: StoppableEvent, data: InputTransformationData<Fragment>) => void,
        options: { priority: 'high' }
    ): void;
}

export interface ClipboardOutputData<Fragment> {
    content: Fragment;
}

export interface ClipboardOutputEmitter<Fragment> {
    on(event: 'clipboardOutput', callback: (evt: unknown, data: ClipboardOutputData<Fragment>) => void): void;
}

// What the editors of this page put on the clipboard (copy, cut and the start
// of a drag), in the form the paste guard sees it when it comes back. Kept in
// memory only. A paste is own content (SanitizeOptions.ownContent) only when
// its HTML is exactly one of these: the trust is bound to the content, so a
// paste from another page or tab, or a copy that was changed on the way, is a
// foreign paste. Nothing on the clipboard itself can claim to be own content.
// Each copy also keeps the rights of the editor it came from (rightsOf), and
// only an editor with the same rights treats it as own content: a copy from a
// CMS editor pasted into a candidate editor on the same page (CMS preview) is
// a foreign paste and loses images, links and figures.
const OWN_CONTENT_LIMIT = 20;
const ownContent: { rights: string; html: string }[] = [];

function rightsOf(options: SanitizeOptions): string {
    return JSON.stringify([
        options.allowImg ?? true,
        options.allowLinks ?? false,
        options.allowFigures ?? false,
        [...(options.allowedClasses ?? [])].sort()
    ]);
}

/**
 * Remembers everything the editor puts on the clipboard as its own content.
 * `normalize` must be what ClipboardPipeline applies to pasted HTML
 * (normalizeClipboardHtml), so the same content gives the same string again.
 */
export function registerOwnContentMarker<Fragment>(
    viewDocument: ClipboardOutputEmitter<Fragment>,
    processor: HtmlProcessor<Fragment>,
    normalize: (html: string) => string,
    config: PasteGuardConfig
): void {
    const rights = rightsOf(config);
    viewDocument.on('clipboardOutput', (_evt, data) => {
        // ClipboardPipeline writes processor.toData(data.content) as text/html.
        // A paste of it reaches the guard as toData(toView(normalize(html))).
        const html = processor.toData(processor.toView(normalize(processor.toData(data.content))));
        if (!html) return;
        const known = ownContent.findIndex(copy => copy.rights === rights && copy.html === html);
        if (known >= 0) ownContent.splice(known, 1);
        ownContent.push({ rights, html });
        if (ownContent.length > OWN_CONTENT_LIMIT) ownContent.shift();
    });
}

function isOwnContent(html: string, rights: string): boolean {
    return html !== '' && ownContent.some(copy => copy.rights === rights && copy.html === html);
}

/**
 * Sanitizes clipboard content before ClipboardPipeline inserts it.
 */
export function registerPasteGuard<Fragment>(
    clipboard: InputTransformationEmitter<Fragment>,
    processor: HtmlProcessor<Fragment>,
    config: PasteGuardConfig,
    sanitizeHtml: (html: string, options: SanitizeOptions) => string = sanitize
): void {
    if (config.mode === 'off') return;
    const rights = rightsOf(config);

    // Must run BEFORE the ClipboardPipeline's default handler, which is at
    // priority 'low' and converts the View fragment to model + inserts it.
    // In CKEditor 5 emitter semantics, higher numeric priority runs first,
    // so 'high' guarantees we mutate data.content before insertion.
    clipboard.on('inputTransformation', (evt, data) => {
        if (!data.content) return;
        try {
            const htmlIn = processor.toData(data.content);
            // Only the content decides about own content, and only the mode
            // about relaxed; neither can be switched on through the config.
            const htmlOut = sanitizeHtml(htmlIn, {
                ...config,
                ownContent: isOwnContent(htmlIn, rights),
                relaxed: currentMode(config) === 'relaxed'
            });
            if (htmlOut === htmlIn) return;
            data.content = processor.toView(htmlOut);
        } catch (err) {
            // Fail closed: unfiltered HTML must never reach the editor. Fall back
            // to the text/plain flavour, which cannot carry markup.
            // eslint-disable-next-line no-console
            console.error('[paste-guard] sanitize failed, pasting plain text instead:', err);
            const text = data.dataTransfer ? data.dataTransfer.getData('text/plain') : '';
            if (!text) {
                evt.stop();
                return;
            }
            try {
                data.content = processor.toView(plainTextToHtml(text));
            } catch (fallbackErr) {
                // Stopping inputTransformation keeps the default handler from inserting anything.
                // eslint-disable-next-line no-console
                console.error('[paste-guard] plain text fallback failed, dropping paste:', fallbackErr);
                evt.stop();
            }
        }
    }, { priority: 'high' });
}

/**
 * Escapes plain text and keeps its line breaks, like ClipboardPipeline does
 * for a paste without text/html.
 */
function plainTextToHtml(text: string): string {
    const html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\r?\n\r?\n/g, '</p><p>')
        .replace(/\r?\n/g, '<br>');
    return html.includes('</p><p>') || html.includes('<br>') ? `<p>${html}</p>` : html;
}
